import { useState, useEffect, useCallback, useRef, MutableRefObject } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";
import {
  MultiFeedOrderbookData,
  MultiplexStreamSubscription,
} from "../sdk/types/api";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import {
  OrderbookConfig,
  OrderbookLevel,
  OrderbookData,
  FastNumberLevel,
} from "../sdk/types/data";

/**
 * Creates an empty orderbook with preserved metadata from current state
 * @param current - Current orderbook data to preserve metadata from
 * @param defaultMarketId - Optional default market ID to use if current.marketIndex is empty
 * @returns Empty orderbook with preserved metadata
 */
function createEmptyOrderbook(
  current: OrderbookData,
  defaultMarketId?: string,
): OrderbookData {
  return {
    asks: [],
    bids: [],
    spread: 0,
    midPrice: 0,
    marketIndex: current.marketIndex || defaultMarketId || "",
    sequenceId: current.sequenceId || "",
    firstUpdateId: current.firstUpdateId || 0,
    lastUpdateId: current.lastUpdateId || 0,
    checksum: current.checksum || "",
    timestamp: current.timestamp || "",
  };
}

/**
 * Convert OrderbookEntry[] to FastNumberLevel
 * Handles both string and number values in the entries
 */
function convertOrderbookEntries(
  entries: Array<[string | number, string | number]>,
): FastNumberLevel {
  return entries.map(([price, size]) => [
    typeof price === "string" ? parseFloat(price) : price,
    typeof size === "string" ? parseFloat(size) : size,
  ]);
}

/**
 * Convert price-size array to OrderbookLevel with cumulative depth
 * Both asks and bids sorted descending (highest to lowest, top to bottom)
 *
 * Note: This is a non-standard sorting (typically asks are ascending, bids descending)
 * but matches the UI display format where:
 * - Asks: highest at top, lowest at bottom (best ask is last element)
 * - Bids: highest at top, lowest at bottom (best bid is first element)
 */
function processLevels(levels: FastNumberLevel): OrderbookLevel[] {
  // Sort: both asks and bids descending (high to low, big to small)
  const sorted = [...levels].sort((a, b) => {
    return b[0] - a[0]; // Descending: highest price first
  });

  // Calculate cumulative depth
  let cumulative = 0;
  return sorted.map(([price, size]) => {
    cumulative += size;
    return {
      price,
      size,
      cumulative,
      flash: null,
    };
  });
}

/**
 * Hook for managing orderbook data with HTTP initialization and WebSocket delta updates
 * Handles data cache management, sorting, and delta updates
 */
export function useOrderbook(config: OrderbookConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  
  // Split OrderbookData into separate states
  const [asks, setAsks] = useState<OrderbookLevel[]>([]);
  const [bids, setBids] = useState<OrderbookLevel[]>([]);
  const [spread, setSpread] = useState<number>(0);
  const [midPrice, setMidPrice] = useState<number>(0);
  const [marketId, setMarketId] = useState<string>("");
  const [sequenceId, setSequenceId] = useState<string>("");
  const [firstUpdateId, setFirstUpdateId] = useState<number>(0);
  const [lastUpdateId, setLastUpdateId] = useState<number>(0);
  const [checksum, setChecksum] = useState<string>("");
  const [timestamp, setTimestamp] = useState<string>("");


  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [wsError, setWsError] = useState<string | null>(null);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const isSubscribedRef = useRef<boolean>(false);
  const currentMarketIdRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);
  const flashTimestampsRef = useRef<Map<string, number>>(new Map());
  const orderbookHandlerRef = useRef<
    (data: MultiFeedOrderbookData & { snapshot?: boolean }) => void
  >(null) as MutableRefObject<
    ((data: MultiFeedOrderbookData & { snapshot?: boolean }) => void) | null
  >;
  const orderbookUnsubscribeRef = useRef<(() => void) | null>(null);
  const orderbookFilterRef = useRef<MultiplexStreamSubscription | null>(null);
  
  // Ref to store current orderbook data (avoids dependency loops)
  const orderbookDataRef = useRef<OrderbookData>({
    asks: [],
    bids: [],
    spread: 0,
    midPrice: 0,
    marketIndex: "",
    sequenceId: "",
    firstUpdateId: 0,
    lastUpdateId: 0,
    checksum: "",
    timestamp: "",
  });

  // Update ref whenever state changes (must match OrderbookData camelCase)
  useEffect(() => {
    orderbookDataRef.current = {
      asks,
      bids,
      spread,
      midPrice,
      marketIndex: marketId,
      sequenceId,
      firstUpdateId,
      lastUpdateId,
      checksum,
      timestamp,
    };
  }, [asks, bids, spread, midPrice, marketId, sequenceId, firstUpdateId, lastUpdateId, checksum, timestamp]);

  /**
   * Apply delta update to orderbook with flash indicators
   * Delta format: { bids: [[price, size]], asks: [[price, size]] }
   * Size 0 means remove the level
   * 
   * Update process (price-based):
   * 1. Scan through current price levels to find matching price
   * 2. Check if the new size is different from the existing size
   * 3. Only update if there's a size difference (or it's a removal)
   * 
   * This ensures updates are applied only when there's an actual change,
   * preventing unnecessary updates when size remains the same.
   */
  const applyDelta = useCallback(
    (
      current: OrderbookData,
      delta: { bids?: FastNumberLevel; asks?: FastNumberLevel },
    ): OrderbookData => {
      const newAsks = new Map<number, number>();
      const newBids = new Map<number, number>();
      const askChanges = new Map<number, "up" | "down">();
      const bidChanges = new Map<number, "up" | "down">();

      // Initialize with current levels
      current.asks.forEach((level) => {
        if (level.size > 0) {
          newAsks.set(level.price, level.size);
        }
      });
      current.bids.forEach((level) => {
        if (level.size > 0) {
          newBids.set(level.price, level.size);
        }
      });

      // Apply delta updates: scan prices, find matches, check size differences
      if (delta.asks) {
        delta.asks.forEach(([price, size]) => {
          // Validate input: skip invalid entries
          if (
            typeof price !== "number" ||
            typeof size !== "number" ||
            isNaN(price) ||
            isNaN(size) ||
            price <= 0 ||
            size < 0
          ) {
            console.warn(
              "[useOrderbook] Invalid delta entry skipped:",
              { price, size },
            );
            return;
          }

          // Step 1: Scan through current asks to find matching price
          const oldSize = newAsks.get(price) ?? 0;
          const priceExists = newAsks.has(price);

          if (size === 0) {
            // Removal: delete the price level (only if it exists)
            // Note: No flash indicator needed for removed prices (they won't be in the final array)
            if (priceExists) {
              newAsks.delete(price);
            }
          } else {
            // Step 2: Check for size difference (or new price)
            const sizeChanged = oldSize !== size;

            if (!priceExists) {
              // New price level: always add it as a delta update
              newAsks.set(price, size);
              askChanges.set(price, "up");
              // Ensure flash is set for new prices
            } else if (sizeChanged) {
              // Existing price: update only if size changed
              newAsks.set(price, size);
              askChanges.set(price, size > oldSize ? "up" : "down");
              // Ensure flash is set for size changes
            }
            // If price exists and size is the same, no update needed (skip - no flash)
          }
        });
      }

      if (delta.bids) {
        delta.bids.forEach(([price, size]) => {
          // Validate input: skip invalid entries
          if (
            typeof price !== "number" ||
            typeof size !== "number" ||
            isNaN(price) ||
            isNaN(size) ||
            price <= 0 ||
            size < 0
          ) {
            console.warn(
              "[useOrderbook] Invalid delta entry skipped:",
              { price, size },
            );
            return;
          }

          // Step 1: Scan through current bids to find matching price
          const oldSize = newBids.get(price) ?? 0;
          const priceExists = newBids.has(price);

          if (size === 0) {
            // Removal: delete the price level (only if it exists)
            // Note: No flash indicator needed for removed prices (they won't be in the final array)
            if (priceExists) {
              newBids.delete(price);
            }
          } else {
            // Step 2: Check for size difference (or new price)
            const sizeChanged = oldSize !== size;

            if (!priceExists) {
              // New price level: always add it as a delta update
              newBids.set(price, size);
              bidChanges.set(price, "up");
              // Ensure flash is set for new prices
            } else if (sizeChanged) {
              // Existing price: update only if size changed
              newBids.set(price, size);
              bidChanges.set(price, size > oldSize ? "up" : "down");
              // Ensure flash is set for size changes
            }
            // If price exists and size is the same, no update needed (skip - no flash)
          }
        });
      }

      // Convert back to arrays and process
      const asksArray: FastNumberLevel = Array.from(newAsks.entries());
      const bidsArray: FastNumberLevel = Array.from(newBids.entries());

      const processedAsks = processLevels(asksArray);
      const processedBids = processLevels(bidsArray);

      // Apply flash indicators and track timestamps
      // All prices that were changed (new or updated) should have flash indicators
      const now = Date.now();
      processedAsks.forEach((level) => {
        const flashValue = askChanges.get(level.price);
        if (flashValue) {
          level.flash = flashValue;
          flashTimestampsRef.current.set(`ask-${level.price}`, now);
        } else {
          // No flash for unchanged prices
          level.flash = null;
        }
      });
      processedBids.forEach((level) => {
        const flashValue = bidChanges.get(level.price);
        if (flashValue) {
          level.flash = flashValue;
          flashTimestampsRef.current.set(`bid-${level.price}`, now);
        } else {
          // No flash for unchanged prices
          level.flash = null;
        }
      });

      // Verification: Log if we have changes that weren't applied (shouldn't happen)
      if (askChanges.size > 0 || bidChanges.size > 0) {
        const appliedAskFlashes = processedAsks.filter((l) => l.flash !== null).length;
        const appliedBidFlashes = processedBids.filter((l) => l.flash !== null).length;
        if (appliedAskFlashes !== askChanges.size || appliedBidFlashes !== bidChanges.size) {
          console.warn(
            "[useOrderbook] Flash indicator mismatch:",
            {
              askChangesCount: askChanges.size,
              appliedAskFlashes,
              bidChangesCount: bidChanges.size,
              appliedBidFlashes,
            },
          );
        }
      }

      // Limit depth if specified
      const maxDepth = config.maxDepth || 100;
      const limitedAsks = processedAsks.slice(0, maxDepth);
      const limitedBids = processedBids.slice(0, maxDepth);

      // Calculate spread and mid price
      // Both asks and bids are descending (first is highest, last is lowest)
      let spread = 0;
      let midPrice = 0;
      if (limitedAsks.length > 0 && limitedBids.length > 0) {
        const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
        const bestBid = limitedBids[0].price; // Highest bid (first in descending)
        spread = bestAsk - bestBid;
        midPrice = (bestAsk + bestBid) / 2;
      }

      return {
        asks: limitedAsks,
        bids: limitedBids,
        spread,
        midPrice,
        marketIndex: current.marketIndex,
        sequenceId: current.sequenceId,
        firstUpdateId: current.firstUpdateId,
        lastUpdateId: current.lastUpdateId,
        checksum: current.checksum,
        timestamp: current.timestamp,
      };
    },
    [config.maxDepth],
  );

  /**
   * Shared function to fetch and process orderbook snapshot from HTTP
   * Used by pollOrderbook, initializeOrderbook, and fetchOrderbookSnapshot
   * Consolidates duplicate logic to ensure only one function handles orderbook fetching
   */
  const fetchAndProcessOrderbook = useCallback(
    async (
      marketId: string,
      options: {
        setLoadingState?: boolean;
        /**
         * Whether to update currentMarketIdRef with the fetched marketId.
         * Set to true when intentionally switching markets (e.g., fetchOrderbookSnapshot).
         * Set to false for polling/initialization to avoid changing the tracked market ID.
         * The ref is used to validate incoming WebSocket updates match the current subscribed market.
         */
        updateMarketIdRef?: boolean;
        errorMessage?: string;
      } = {},
    ): Promise<OrderbookData | void> => {
      if (!infoClientRef.current) {
        if (options.errorMessage) {
          console.error(options.errorMessage);
        }
        return;
      }

      const { setLoadingState = false, updateMarketIdRef = false } = options;

      if (setLoadingState) {
        setLoading(true);
        setError(null);
      }


      try {
        const data = await infoClientRef.current.getOrderbook(marketId, true);

        if (data && (data.bids || data.asks)) {
          const asksArray: FastNumberLevel = convertOrderbookEntries(
            data.asks || [],
          );
          const bidsArray: FastNumberLevel = convertOrderbookEntries(
            data.bids || [],
          );

          const processedAsks = processLevels(asksArray);
          const processedBids = processLevels(bidsArray);

          // Limit depth if specified
          const maxDepth = config.maxDepth || 100;
          const limitedAsks = processedAsks.slice(0, maxDepth);
          const limitedBids = processedBids.slice(0, maxDepth);

          // Calculate spread and mid price
          // Both asks and bids are descending (first is highest, last is lowest)
          let spread = 0;
          let midPrice = 0;
          if (limitedAsks.length > 0 && limitedBids.length > 0) {
            const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
            const bestBid = limitedBids[0].price; // Highest bid (first in descending)
            spread = bestAsk - bestBid;
            midPrice = (bestAsk + bestBid) / 2;
          }

          // Update current market ID ref if requested
          // This ref tracks the market ID we're subscribed to and is used to validate
          // incoming WebSocket updates match the current market before applying deltas
          if (updateMarketIdRef) {
            currentMarketIdRef.current = marketId;
          }

          // Get current state to preserve metadata
          const current = orderbookDataRef.current;
          const newState: OrderbookData = {
            asks: limitedAsks,
            bids: limitedBids,
            spread,
            midPrice,
            marketIndex: marketId,
            sequenceId: current.sequenceId || "",
            firstUpdateId: current.firstUpdateId || 0,
            lastUpdateId: current.lastUpdateId || 0,
            checksum: current.checksum || "",
            timestamp: current.timestamp || new Date().toISOString(),
          };
          setAsks(limitedAsks);
          setBids(limitedBids);
          setSpread(spread);
          setMidPrice(midPrice);
          setMarketId(marketId);
          setSequenceId(newState.sequenceId);
          setFirstUpdateId(newState.firstUpdateId);
          setLastUpdateId(newState.lastUpdateId);
          setChecksum(newState.checksum);
          setTimestamp(newState.timestamp);
          return newState;
        }
      } catch (err) {
        const errorMsg =
          options.errorMessage ||
          (err instanceof Error ? err.message : "Failed to load orderbook");
        if (setLoadingState) {
          setError(errorMsg);
        } else {
          // Only log error if we don't have existing data (to avoid overwriting with transient errors)
          console.error(errorMsg);
        }
      } finally {
        if (setLoadingState) {
          setLoading(false);
        }
      }
    },
    [config.maxDepth],
  );

  /**
   * Poll orderbook from HTTP request (for fallback when WebSocket fails)
   * Doesn't set loading state to avoid UI flicker during polling
   */
  const pollOrderbook = useCallback(async () => {
    // Clear asks and bids, preserve metadata
    const current = orderbookDataRef.current;
    const empty = createEmptyOrderbook(current);
    setAsks(empty.asks);
    setBids(empty.bids);
    setSpread(empty.spread);
    setMidPrice(empty.midPrice);
    await fetchAndProcessOrderbook(config.marketId, {
      setLoadingState: false,
      // Don't update market ID ref during polling - we're just refreshing current market
      updateMarketIdRef: false,
    });
  }, [config.marketId, fetchAndProcessOrderbook]);

  /**
   * Initialize orderbook from HTTP request
   */
  const initializeOrderbook = useCallback(async () => {
    // Clear asks and bids, set market_id to config.marketId, preserve other metadata
    const current = orderbookDataRef.current;
    const empty = createEmptyOrderbook(current, config.marketId);
    setAsks(empty.asks);
    setBids(empty.bids);
    setSpread(empty.spread);
    setMidPrice(empty.midPrice);
    setMarketId(empty.marketIndex);
    await fetchAndProcessOrderbook(config.marketId, {
      setLoadingState: true,
      // Don't update market ID ref during initialization - ref is set separately on mount
      updateMarketIdRef: false,
      errorMessage: "Failed to load orderbook",
    });
  }, [config.marketId, fetchAndProcessOrderbook]);

  /**
   * Fetch orderbook snapshot for a given market_id and update state.
   * Returns the new orderbook state so callers can use it as "current" for deltas
   * without relying on refs (which lag until after React commits state).
   */
  const fetchOrderbookSnapshot = useCallback(
    async (marketId: string): Promise<OrderbookData | void> => {
      const newState = await fetchAndProcessOrderbook(marketId, {
        setLoadingState: false,
        // Update market ID ref when switching markets - ensures WebSocket updates
        // for the new market are correctly validated and processed
        updateMarketIdRef: true,
        errorMessage: `Failed to fetch snapshot for market ${marketId}`,
      });
      if (newState) {
        console.log(
          `[useOrderbook] Fetched snapshot for market ${marketId} and updated orderbook`,
        );
      }
      return newState;
    },
    [fetchAndProcessOrderbook],
  );

  /**
   * Process orderbook update (snapshot or delta)
   * Delta updates are always price-based - matching by price level
   */
  const processOrderbookUpdate = useCallback(
    (data: any, current: OrderbookData): OrderbookData => {
      // Only treat as snapshot if explicitly marked
      // All other updates are price-based deltas
      const isSnapshot =
        data.snapshot === true || data.type === "snapshot";

      if (isSnapshot) {
        // Full snapshot - replace entire orderbook
        const asksArray: FastNumberLevel = convertOrderbookEntries(
          data.asks || [],
        );
        const bidsArray: FastNumberLevel = convertOrderbookEntries(
          data.bids || [],
        );

        const processedAsks = processLevels(asksArray);
        const processedBids = processLevels(bidsArray);

        const maxDepth = config.maxDepth || 100;
        const limitedAsks = processedAsks.slice(0, maxDepth);
        const limitedBids = processedBids.slice(0, maxDepth);

        // Calculate spread and mid price
        // Both asks and bids are descending (first is highest, last is lowest)
        let spread = 0;
        let midPrice = 0;
        if (limitedAsks.length > 0 && limitedBids.length > 0) {
          const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
          const bestBid = limitedBids[0].price; // Highest bid (first in descending)
          spread = bestAsk - bestBid;
          midPrice = (bestAsk + bestBid) / 2;
        }

        return {
          asks: limitedAsks,
          bids: limitedBids,
          spread,
          midPrice,
          marketIndex: data.marketIndex ?? current.marketIndex,
          sequenceId: data.sequenceId || current.sequenceId,
          firstUpdateId: data.firstUpdateId ?? current.firstUpdateId,
          lastUpdateId: data.lastUpdateId ?? current.lastUpdateId,
          checksum: data.checksum || current.checksum,
          timestamp: data.timestamp || current.timestamp,
          snapshot: data.snapshot,
        };
      } else {
        const result = applyDelta(current, {
          bids: data.bids,
          asks: data.asks,
        });
        return {
          ...result,
          marketIndex: data.marketIndex ?? result.marketIndex,
          sequenceId: data.sequenceId || result.sequenceId,
          firstUpdateId: data.firstUpdateId ?? result.firstUpdateId,
          lastUpdateId: data.lastUpdateId ?? result.lastUpdateId,
          checksum: data.checksum || result.checksum,
          timestamp: data.timestamp || result.timestamp,
        };
      }
    },
    [applyDelta, config.maxDepth],
  );

  /**
   * Handle WebSocket orderbook update
   * Handles multi-feed format (MultiFeedOrderbookData)
   * Converts all satoshi values to decimal numbers
   * Validates market_id and fetches snapshot if market_id doesn't match current market
   */
  const handleOrderbookUpdate = useCallback(
    async (data: MultiFeedOrderbookData & { snapshot?: boolean }) => {
      if (!data || (!data.bids && !data.asks)) {
        console.log("[useOrderbook] Invalid data: missing bids and asks");
        return;
      }

      // Only process multi-feed format (must have marketIndex and sequenceId; accept snake_case from wire)
      const seqId = data.sequenceId ?? (data as { sequence_id?: string }).sequence_id;
      if (!data.marketIndex || seqId === undefined) {
        console.log(
          "[useOrderbook] Rejected: not multi-feed format (missing marketIndex or sequenceId)",
          {
            hasMarketId: !!data.marketIndex,
            hasSequenceId: seqId !== undefined,
          },
        );
        return;
      }

      // Validate marketIndex to ensure this update is for the current market
      const currentMarketId = currentMarketIdRef.current || config.marketId;
      let currentForUpdate = orderbookDataRef.current;

      if (data.marketIndex !== currentMarketId) {
        console.log(
          `[useOrderbook] Received update for market ${data.marketIndex}, current market is ${currentMarketId}. Fetching snapshot...`,
        );
        // Fetch latest snapshot for the new market; use returned state as current
        // so we apply the delta to the snapshot (ref is not updated until after commit)
        const snapshotState = await fetchOrderbookSnapshot(data.marketIndex);
        if (snapshotState) currentForUpdate = snapshotState;
      }

      // Convert satoshi to decimal for multi-feed format (proto camelCase; accept snake_case from wire)
      const raw = data as MultiFeedOrderbookData & { sequence_id?: string; first_update_id?: number; last_update_id?: number };
      type OrderbookUpdatePayload = Omit<OrderbookData, "asks" | "bids"> & {
        asks: FastNumberLevel;
        bids: FastNumberLevel;
      };
      const transformedData: OrderbookUpdatePayload = {
        marketIndex: data.marketIndex ?? "",
        sequenceId: data.sequenceId ?? raw.sequence_id ?? "",
        firstUpdateId: data.firstUpdateId ?? raw.first_update_id ?? 0,
        lastUpdateId: data.lastUpdateId ?? raw.last_update_id ?? 0,
        checksum: data.checksum ?? "",
        timestamp: data.timestamp ?? "",
        bids: (data.bids ?? []).map(([price, quantity]) => [
          toNumberFromSatoshi(price),
          toNumberFromSatoshi(quantity),
        ]),
        asks: (data.asks ?? []).map(([price, quantity]) => [
          toNumberFromSatoshi(price),
          toNumberFromSatoshi(quantity),
        ]),
        spread: 0,
        midPrice: 0,
        snapshot: data.snapshot === true,
      };

      console.log("[useOrderbook] Processing orderbook update", {
        marketId: transformedData.marketIndex,
        sequenceId: transformedData.sequenceId,
        bidsCount: transformedData.bids.length,
        asksCount: transformedData.asks.length,
        isSnapshot: transformedData.snapshot,
      });

      // Process update immediately (use currentForUpdate so post-snapshot delta uses snapshot state)
      const result = processOrderbookUpdate(transformedData, currentForUpdate);

      // Note: We don't skip updates here because:
      // 1. The delta logic already skips updates when size doesn't change
      // 2. React's reconciliation will handle unnecessary re-renders efficiently
      // 3. Skipping could miss legitimate updates to internal levels

      console.log("[useOrderbook] Orderbook updated successfully", {
        sequenceId: transformedData.sequenceId,
        bidsCount: result.bids.length,
        asksCount: result.asks.length,
        spread: result.spread,
        midPrice: result.midPrice,
        bestBid: result.bids.length > 0 ? result.bids[0].price : null,
        bestAsk:
          result.asks.length > 0
            ? result.asks[result.asks.length - 1].price
            : null,
      });

      setAsks(result.asks);
      setBids(result.bids);
      setSpread(result.spread);
      setMidPrice(result.midPrice);
      setMarketId(result.marketIndex);
      setSequenceId(result.sequenceId);
      setFirstUpdateId(result.firstUpdateId);
      setLastUpdateId(result.lastUpdateId);
      setChecksum(result.checksum);
      setTimestamp(result.timestamp);
    },
    [processOrderbookUpdate, fetchOrderbookSnapshot, config.marketId],
  );
  orderbookHandlerRef.current = handleOrderbookUpdate;

  // Initialize clients (only when connection config changes, not marketId)
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    infoClientRef.current = infoClient;

    // Use the provided subscription client instead of creating a new one
    subscriptionClientRef.current = config.subscriptionClient;

    // Initialize market ID ref on mount
    if (currentMarketIdRef.current === null) {
      currentMarketIdRef.current = config.marketId;
    }

    // Subscribe function that can be called on initial connect or reconnection
    // Uses currentMarketIdRef to always get the latest market ID
    const subscribe = () => {
      const subscriptionClient = subscriptionClientRef.current;
      if (!subscriptionClient) return;

      if (subscriptionClient.isConnected()) {
        const marketIdToSubscribe =
          currentMarketIdRef.current !== null
            ? currentMarketIdRef.current
            : config.marketId; // Fallback to config.marketId on initial mount

        const marketIdNum = Number(marketIdToSubscribe);
        if (Number.isNaN(marketIdNum)) return;

        const multiplex = subscriptionClient.getMultiplexStreamClient();

        // Subscribe to current market if not already subscribed
        if (
          !isSubscribedRef.current ||
          currentMarketIdRef.current !== marketIdToSubscribe
        ) {
          // Unsubscribe from previous market if different and subscribed
          if (orderbookFilterRef.current) {
            multiplex.unsubscribeStreams([orderbookFilterRef.current]);
            orderbookFilterRef.current = null;
          }
          const sub: MultiplexStreamSubscription = {
            stream_type: "orderbook",
            filter: { marketIndices: [marketIdNum], depth: 20 },
          };
          multiplex.subscribeStreams([sub]).catch(console.error);
          orderbookFilterRef.current = sub;
          isSubscribedRef.current = true;
          currentMarketIdRef.current = marketIdToSubscribe;
        }
      }
    };

    // Check initial connection state and subscribe if WebSocket is enabled
    const subscriptionClient = config.subscriptionClient;

    if (useWebSocket) {
      const multiplex = subscriptionClient.getMultiplexStreamClient();
      orderbookUnsubscribeRef.current = multiplex.onStreamData(
        "orderbook",
        (resp) => {
          if (resp.type === "data" && resp.data != null) {
            orderbookHandlerRef.current?.(
              resp.data as MultiFeedOrderbookData & { snapshot?: boolean },
            );
          }
        },
      );

      const isInitiallyConnected = subscriptionClient.isConnected();

      if (isInitiallyConnected) {
        setIsConnected(true);
        setWsError(null);
        subscribe();
      } else {
        // If not connected, set up polling fallback
        setWsError("WebSocket not connected. Using HTTP polling.");
        setIsConnected(false);
        if (infoClientRef.current && pollingIntervalRef.current === null) {
          pollOrderbook();
          pollingIntervalRef.current = window.setInterval(() => {
            pollOrderbook();
          }, 5000);
        }
      }

      // Monitor connection state changes and re-subscribe on reconnection
      // Use the public onStateChange method from SubscriptionClient
      const unsubscribeStateChange = subscriptionClient.onStateChange(
        (state: ConnectionState) => {
          if (state === ConnectionState.Connected) {
            setIsConnected(true);
            setWsError(null); // Clear WebSocket error on successful connection
            // Clear polling if WebSocket connects
            if (pollingIntervalRef.current !== null) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            // Re-subscribe if we were previously subscribed
            if (isSubscribedRef.current) {
              subscribe();
            }
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            setIsConnected(false);
            isSubscribedRef.current = false;
            // Set WebSocket error on disconnect (component will only show if data exists)
            setWsError("WebSocket disconnected. Using static data.");
          }
        },
      );

      // Store unsubscribe function for cleanup
      const storedUnsubscribeStateChange = unsubscribeStateChange;

      return () => {
        // Clear connection timeout
        if (connectionTimeoutRef.current !== null) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        isSubscribedRef.current = false;
        currentMarketIdRef.current = null;

        // Unsubscribe from state changes
        if (storedUnsubscribeStateChange) {
          storedUnsubscribeStateChange();
        }

        // Unsubscribe from orderbook stream and remove data callback
        if (subscriptionClientRef.current) {
          const mux = subscriptionClientRef.current.getMultiplexStreamClient();
          if (orderbookFilterRef.current) {
            mux.unsubscribeStreams([orderbookFilterRef.current]);
            orderbookFilterRef.current = null;
          }
        }
        orderbookUnsubscribeRef.current?.();
        orderbookUnsubscribeRef.current = null;
      };
    } else {
      // WebSocket disabled - use polling only
      setIsConnected(false);
      setWsError(null);
      if (infoClientRef.current && pollingIntervalRef.current === null) {
        pollOrderbook();
        pollingIntervalRef.current = window.setInterval(() => {
          pollOrderbook();
        }, 5000);
      }

      return () => {
        // Clear polling interval
        if (pollingIntervalRef.current !== null) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        isSubscribedRef.current = false;
        currentMarketIdRef.current = null;
      };
    }
  }, [config.baseURL, config.subscriptionClient, useWebSocket]);

  // Handle market ID changes: re-initialize orderbook and re-subscribe to WS
  useEffect(() => {
    if (!infoClientRef.current) {
      return;
    }

    const previousMarketId = currentMarketIdRef.current;

    // Unsubscribe from previous market if it exists and is different (only if WebSocket is enabled)
    if (
      useWebSocket &&
      subscriptionClientRef.current &&
      previousMarketId !== null &&
      previousMarketId !== config.marketId &&
      subscriptionClientRef.current.isConnected()
    ) {
      const multiplex = subscriptionClientRef.current.getMultiplexStreamClient();
      if (orderbookFilterRef.current) {
        multiplex.unsubscribeStreams([orderbookFilterRef.current]);
        orderbookFilterRef.current = null;
      }
      isSubscribedRef.current = false;
    }

    // Clear existing orderbook data when switching markets
    setAsks([]);
    setBids([]);
    setSpread(0);
    setMidPrice(0);
    setMarketId(config.marketId);
    setSequenceId("");
    setFirstUpdateId(0);
    setLastUpdateId(0);
    setChecksum("");
    setTimestamp("");
    setLoading(true);
    setError(null);
    setWsError(null);

    // Initialize new market orderbook from REST
    initializeOrderbook();

    // Subscribe to new market WebSocket updates via multiplex (only if WebSocket is enabled)
    if (
      useWebSocket &&
      subscriptionClientRef.current &&
      subscriptionClientRef.current.isConnected()
    ) {
      const marketIdNum = Number(config.marketId);
      if (!Number.isNaN(marketIdNum)) {
        const multiplex =
          subscriptionClientRef.current.getMultiplexStreamClient();
        const sub: MultiplexStreamSubscription = {
          stream_type: "orderbook",
          filter: { marketIndices: [marketIdNum], depth: 20 },
        };
        multiplex.subscribeStreams([sub]).catch(console.error);
        orderbookFilterRef.current = sub;
        isSubscribedRef.current = true;
      }
    }

    // Update ref to track current market ID
    currentMarketIdRef.current = config.marketId;

    // Cleanup function: unsubscribe when marketId changes or component unmounts
    return () => {
      if (
        useWebSocket &&
        subscriptionClientRef.current &&
        subscriptionClientRef.current.isConnected() &&
        currentMarketIdRef.current === config.marketId
      ) {
        const multiplex =
          subscriptionClientRef.current.getMultiplexStreamClient();
        if (orderbookFilterRef.current) {
          multiplex.unsubscribeStreams([orderbookFilterRef.current]);
          orderbookFilterRef.current = null;
        }
        isSubscribedRef.current = false;
      }
    };
  }, [config.marketId, useWebSocket, initializeOrderbook]);

  // Fallback to HTTP polling when WebSocket is disconnected (unless disabled or WebSocket is disabled)
  useEffect(() => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Start polling if:
    // 1. WebSocket is disabled, OR
    // 2. WebSocket is enabled but not connected and polling is not disabled
    if (
      (!useWebSocket || (!isConnected && useWebSocket)) &&
      infoClientRef.current &&
      !config.disablePolling
    ) {
      // Start polling immediately, then every 30 seconds
      pollOrderbook();
      pollingIntervalRef.current = window.setInterval(() => {
        pollOrderbook();
      }, 30000);
    }

    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    isConnected,
    useWebSocket,
    config.marketId,
    config.disablePolling,
    pollOrderbook,
  ]);

  // Clear flash indicators after display duration (500ms per level)
  useEffect(() => {
    const flashTimer = setInterval(() => {
      const now = Date.now();
      const flashTimeout = 500; // Clear flashes after 500ms

      let hasChanges = false;
      const newAsks = asks.map((level) => {
        const key = `ask-${level.price}`;
        const flashTime = flashTimestampsRef.current.get(key);

        if (level.flash && flashTime && now - flashTime >= flashTimeout) {
          hasChanges = true;
          flashTimestampsRef.current.delete(key);
          return { ...level, flash: null };
        }
        return level;
      });

      const newBids = bids.map((level) => {
        const key = `bid-${level.price}`;
        const flashTime = flashTimestampsRef.current.get(key);

        if (level.flash && flashTime && now - flashTime >= flashTimeout) {
          hasChanges = true;
          flashTimestampsRef.current.delete(key);
          return { ...level, flash: null };
        }
        return level;
      });

      // Only update if there are actual changes to avoid unnecessary re-renders
      if (hasChanges) {
        setAsks(newAsks);
        setBids(newBids);
      }
    }, 100); // Check every 100ms for more responsive clearing

    return () => clearInterval(flashTimer);
  }, [asks, bids]);



  return {
    asks,
    bids,
    spread,
    midPrice,
    market_id: marketId,
    sequence_id: sequenceId,
    first_update_id: firstUpdateId,
    last_update_id: lastUpdateId,
    checksum,
    timestamp,
    loading,
    error,
    isConnected,
    wsError,
    refresh: initializeOrderbook,
  };
}
